import React, { useEffect, useState } from "react";
import { divIcon } from "leaflet";
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { normaliseMapPosition } from "./locationUtils";

const locationPinIcon = divIcon({
  className: "location-picker-marker",
  html: '<span aria-hidden="true">&#128205;</span>',
  iconSize: [40, 40],
  iconAnchor: [20, 38],
});

const ukCenter = [54.5, -3];

function normalisePosition(value) {
  return normaliseMapPosition(value?.latitude, value?.longitude);
}

function MapInteraction({ disabled, onChange }) {
  useMapEvents({
    click(event) {
      if (disabled) return;
      onChange({
        latitude: Number(event.latlng.lat.toFixed(7)),
        longitude: Number(event.latlng.lng.toFixed(7)),
      });
    },
  });
  return null;
}

function MapPosition({ position }) {
  const map = useMap();
  const latitude = position?.latitude;
  const longitude = position?.longitude;

  useEffect(() => {
    if (latitude == null || longitude == null) return;
    map.flyTo([latitude, longitude], Math.max(map.getZoom(), 16));
  }, [map, latitude, longitude]);

  return null;
}

export default function OpeningHoursLocationPicker({
  value,
  disabled = false,
  onChange,
  legend = "Delivery location (optional)",
  description = "Click the map or drag the pin to show our transport team the exact delivery point.",
}) {
  const headingId = React.useId();
  const [locationState, setLocationState] = useState("idle");
  const [locationMessage, setLocationMessage] = useState("");
  const position = normalisePosition(value);
  const mapCenter = position
    ? [position.latitude, position.longitude]
    : ukCenter;

  const useDeviceLocation = () => {
    if (!navigator.geolocation) {
      setLocationState("error");
      setLocationMessage("Location is not available on this device.");
      return;
    }

    setLocationState("loading");
    setLocationMessage("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        onChange({
          latitude: Number(coords.latitude.toFixed(7)),
          longitude: Number(coords.longitude.toFixed(7)),
        });
        setLocationState("success");
        setLocationMessage("Pin updated to your current location.");
      },
      () => {
        setLocationState("error");
        setLocationMessage(
          "We could not access your location. You can still place the pin on the map."
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  return (
    <section className="opening-hours-location" aria-labelledby={headingId}>
      <h3 id={headingId}>{legend}</h3>
      <div className="opening-hours-location__heading">
        <p>{description}</p>
        <button
          type="button"
          className="button-secondary"
          onClick={useDeviceLocation}
          disabled={disabled || locationState === "loading"}
        >
          {locationState === "loading" ? "Locating..." : "Use my location"}
        </button>
      </div>

      <MapContainer
        center={mapCenter}
        zoom={position ? 16 : 5}
        scrollWheelZoom
        className="opening-hours-location__map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapInteraction disabled={disabled} onChange={onChange} />
        <MapPosition position={position} />
        {position && (
          <Marker
            position={[position.latitude, position.longitude]}
            icon={locationPinIcon}
            draggable={!disabled}
            eventHandlers={{
              dragend(event) {
                const point = event.target.getLatLng();
                onChange({
                  latitude: Number(point.lat.toFixed(7)),
                  longitude: Number(point.lng.toFixed(7)),
                });
              },
            }}
          />
        )}
      </MapContainer>

      <div className="opening-hours-location__status" aria-live="polite">
        <span>
          {position
            ? `Pin: ${position.latitude.toFixed(7)}, ${position.longitude.toFixed(7)}`
            : "No exact pin selected yet. Click the map to place one."}
        </span>
        {locationMessage && (
          <span className={locationState === "error" ? "is-error" : ""}>
            {locationMessage}
          </span>
        )}
      </div>
    </section>
  );
}
